import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Clock, MapPin, Music, Users, Calendar, CheckCircle, XCircle, HelpCircle } from 'lucide-react';
import { API_URL } from '../services/ApiService';
import { formatDateTime, formatTime } from '../utils/dateFormatter';

const PublicRSVPPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [userRsvpToken, setUserRsvpToken] = useState(localStorage.getItem(`rsvp_${token}`));
  
  const [formData, setFormData] = useState({
    name: localStorage.getItem('rsvp_name') || '',
    email: localStorage.getItem('rsvp_email') || '',
    status: '',
    reason: ''
  });

  useEffect(() => {
    fetchEvent();
  }, [token]);

  const fetchEvent = async () => {
    try {
      const response = await fetch(`${API_URL}/events/public/${token}`);
      const data = await response.json();
      
      if (data.success) {
        setEvent(data.data);
        
        // Check if user already RSVP'd (by checking local storage)
        const savedName = localStorage.getItem('rsvp_name');
        const savedEmail = localStorage.getItem('rsvp_email');
        if (savedName && data.data.publicRsvps) {
          const existingRsvp = data.data.publicRsvps.find(
            r => r.name === savedName && (!savedEmail || r.email === savedEmail)
          );
          if (existingRsvp) {
            setFormData({
              ...formData,
              status: existingRsvp.status,
              reason: existingRsvp.reason || ''
            });
          }
        }
      } else {
        setError(data.error || 'Event not found');
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      setError('Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.status) {
      setError('Please enter your name and select a response');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/events/public/${token}/rsvp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Save info to local storage for future RSVPs
        localStorage.setItem('rsvp_name', formData.name);
        if (formData.email) {
          localStorage.setItem('rsvp_email', formData.email);
        }
        if (data.rsvpToken) {
          localStorage.setItem(`rsvp_${token}`, data.rsvpToken);
          setUserRsvpToken(data.rsvpToken);
        }
        
        setSubmitted(true);
        // Refresh event to show updated RSVPs
        fetchEvent();
      } else {
        setError(data.error || 'Failed to submit RSVP');
      }
    } catch (error) {
      console.error('Error submitting RSVP:', error);
      setError('Failed to submit RSVP');
    } finally {
      setLoading(false);
    }
  };

  const getEventTypeStyle = (type) => {
    const styles = {
      rehearsal: 'bg-green-100 text-green-800 border-green-200',
      concert: 'bg-orange-100 text-orange-800 border-orange-200',
      recording: 'bg-purple-100 text-purple-800 border-purple-200',
      meeting: 'bg-blue-100 text-blue-800 border-blue-200',
      audition: 'bg-red-100 text-red-800 border-red-200',
      default: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return styles[type] || styles.default;
  };

  if (loading && !event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mb-4"></div>
          <p className="text-muted-foreground">Loading event...</p>
        </div>
      </div>
    );
  }

  if (error && !event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <XCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <h2 className="text-xl font-semibold mb-2">Event Not Found</h2>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  const getRsvpCounts = () => {
    if (!event?.publicRsvps) return { confirmed: 0, declined: 0, maybe: 0 };
    
    return {
      confirmed: event.publicRsvps.filter(r => r.status === 'confirmed').length,
      declined: event.publicRsvps.filter(r => r.status === 'declined').length,
      maybe: event.publicRsvps.filter(r => r.status === 'maybe').length
    };
  };

  const counts = getRsvpCounts();

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto p-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Event RSVP</h1>
          {event?.band?.name && (
            <p className="text-muted-foreground">
              <Users className="inline-block w-4 h-4 mr-1" />
              {event.band.name}
            </p>
          )}
        </div>

        {/* Event Details Card */}
        <div className="bg-card rounded-lg border border-border p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-2xl font-semibold">{event?.title}</h2>
            <span className={`text-xs px-2 py-1 rounded border ${getEventTypeStyle(event?.type)}`}>
              {event?.type}
            </span>
          </div>

          {event?.description && (
            <p className="text-muted-foreground mb-4">{event.description}</p>
          )}

          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span>{formatDateTime(event?.startTime)}</span>
            </div>
            
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span>{formatTime(event?.startTime)} - {formatTime(event?.endTime)}</span>
            </div>

            {event?.location?.name && (
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div>
                  <div>{event.location.name}</div>
                  {event.location.address && (
                    <div className="text-muted-foreground text-xs">{event.location.address}</div>
                  )}
                </div>
              </div>
            )}

            {event?.setlist && (
              <div className="flex items-center gap-2 text-sm">
                <Music className="w-4 h-4 text-muted-foreground" />
                <span>Setlist: {event.setlist.name}</span>
              </div>
            )}
          </div>

          {/* Current RSVPs */}
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Current RSVPs:</span>
              <div className="flex gap-4">
                <span className="text-green-600">
                  <CheckCircle className="inline-block w-3 h-3 mr-1" />
                  {counts.confirmed} Going
                </span>
                <span className="text-red-600">
                  <XCircle className="inline-block w-3 h-3 mr-1" />
                  {counts.declined} Not Going
                </span>
                <span className="text-yellow-600">
                  <HelpCircle className="inline-block w-3 h-3 mr-1" />
                  {counts.maybe} Maybe
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* RSVP Form */}
        {!submitted ? (
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold mb-4">Your Response</h3>
            
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Your Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter your name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Email (Optional)</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="your@email.com"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  We'll only use this for event updates
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Will you attend? *</label>
                <div className="space-y-2">
                  <label className="flex items-center p-3 border rounded-md cursor-pointer hover:bg-muted/50">
                    <input
                      type="radio"
                      name="status"
                      value="confirmed"
                      checked={formData.status === 'confirmed'}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="mr-3"
                    />
                    <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                    <span>Yes, I'll be there!</span>
                  </label>

                  <label className="flex items-center p-3 border rounded-md cursor-pointer hover:bg-muted/50">
                    <input
                      type="radio"
                      name="status"
                      value="declined"
                      checked={formData.status === 'declined'}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="mr-3"
                    />
                    <XCircle className="w-4 h-4 mr-2 text-red-600" />
                    <span>No, I can't make it</span>
                  </label>

                  <label className="flex items-center p-3 border rounded-md cursor-pointer hover:bg-muted/50">
                    <input
                      type="radio"
                      name="status"
                      value="maybe"
                      checked={formData.status === 'maybe'}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="mr-3"
                    />
                    <HelpCircle className="w-4 h-4 mr-2 text-yellow-600" />
                    <span>Maybe</span>
                  </label>
                </div>
              </div>

              {formData.status === 'declined' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Reason (Optional)</label>
                  <textarea
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    rows="2"
                    placeholder="Let them know why you can't make it..."
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !formData.status}
                className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting...' : 'Submit RSVP'}
              </button>
            </form>
          </div>
        ) : (
          <div className="bg-card rounded-lg border border-border p-6 text-center">
            <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-600" />
            <h3 className="text-lg font-semibold mb-2">RSVP Submitted!</h3>
            <p className="text-muted-foreground mb-4">
              Thank you for your response, {formData.name}.
            </p>
            <button
              onClick={() => {
                setSubmitted(false);
                setFormData({ ...formData, status: '', reason: '' });
              }}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
            >
              Update My Response
            </button>
          </div>
        )}

        {/* Attendee List */}
        {event?.publicRsvps && event.publicRsvps.length > 0 && (
          <div className="bg-card rounded-lg border border-border p-6 mt-6">
            <h3 className="text-lg font-semibold mb-4">Who's Coming</h3>
            <div className="space-y-2">
              {event.publicRsvps.map((rsvp, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <span className="font-medium">{rsvp.name}</span>
                  <span className={`text-sm ${
                    rsvp.status === 'confirmed' ? 'text-green-600' :
                    rsvp.status === 'declined' ? 'text-red-600' :
                    'text-yellow-600'
                  }`}>
                    {rsvp.status === 'confirmed' ? 'Going' :
                     rsvp.status === 'declined' ? 'Not Going' :
                     'Maybe'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicRSVPPage;